import React from 'react';
import { Dimensions, Platform, StyleSheet, View } from 'react-native';

import { BigButton } from '@components/Button';
import Divider from '@components/Divider';
import Icon from '@components/Icon';
import Placeholder, { EmptySearchPlaceholder } from '@components/Placeholders';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import SearchBar from '@components/SearchBar';
import { Route, TabView } from '@components/TabView';
import { PrimaryHeadline, Subheading } from '@components/Typography';
import { UserData } from '@components/User/Card';
import { LargeUserCardList } from '@components/User/CardList';
import { LoadingView, Row } from '@components/View';
import { useFollowing } from '@context/FollowingContext';
import { useRecommendations } from '@context/RecommendationsContext';
import { useUserSearch } from '@db/Search';
import { createAppNavigation } from '@navigation/AppNavigation';
import { useHeader } from '@navigation/Header';
import type { FollowUsersScreenProps as NavigationFollowUsersScreenProps } from '@navigation/Types';

enum UserTab {
  NEW = 'NEW',
  FOLLOWING = 'FOLLOWING',
}

type FollowUsersScreenProps = {
  navigation: NavigationFollowUsersScreenProps['navigation'];
  route?: NavigationFollowUsersScreenProps['route'];
};

export default function FollowUsersScreen({ navigation, route }: FollowUsersScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  useHeader({ appNavigation, navigation });

  const [searchText, setSearchText] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<UserTab>(UserTab.NEW);
  const { followingUserIds, followingUsers } = useFollowing();
  const { recommendedFollowUsers } = useRecommendations();
  const isOnboarding = route?.params?.screenType === 'onboarding';
  const activeSearchTab = isOnboarding ? UserTab.NEW : activeTab;
  const followingUserIdList = React.useMemo(() => [...followingUserIds], [followingUserIds]);
  const { loading, results: usersResult } = useUserSearch({
    query: searchText,
    includedUserIds: activeSearchTab === UserTab.FOLLOWING ? followingUserIdList : undefined,
    excludedUserIds: activeSearchTab === UserTab.NEW ? followingUserIdList : undefined,
  });

  const usersToFollow = React.useMemo(() => {
    return recommendedFollowUsers.filter((user) => !followingUserIds.has(user.userId));
  }, [followingUserIds, recommendedFollowUsers]);
  const newUsers = searchText ? usersResult : usersToFollow;
  const existingUsers = searchText ? usersResult : followingUsers;

  const onPressUser = (user: UserData) => {
    if (!isOnboarding) {
      appNavigation.openProfile(user.userId);
    }
  };

  const renderEmptyComponent = (emptyTitle: string) => (
    // Note: loading is done here specifically to
    // prevent the entire screen re-rendering
    <LoadingView loading={loading} indicatorSize="large">
      {searchText ? (
        <EmptySearchPlaceholder />
      ) : (
        <Placeholder title={emptyTitle} icon="account-multiple" />
      )}
    </LoadingView>
  );

  const renderHeaderComponent = () => (
    <>
      <SearchBar
        value={searchText}
        onChangeText={(text) => setSearchText(text)}
        loading={loading}
        style={isOnboarding ? styles.onboardingSearchBar : styles.searchBar}
        placeholder="Search people"
      />
      {!isOnboarding && Platform.OS !== 'web' ? (
        <Row style={styles.inviteContactsContainer}>
          <PressableOpacity onPress={appNavigation.openInviteContacts}>
            <Row style={styles.inviteContactsButton}>
              <Icon name="account-plus" />
              <Subheading style={styles.inviteContactsText}>Invite Your Friends</Subheading>
              <Icon name="chevron-right" />
            </Row>
          </PressableOpacity>
        </Row>
      ) : null}
    </>
  );

  const renderUserList = (users: UserData[], emptyTitle: string) => (
    <LargeUserCardList
      data={users}
      itemProps={{
        onPress: onPressUser,
        messageButton: !isOnboarding
          ? {
              onPress: appNavigation.openChat,
            }
          : undefined,
      }}
      ListEmptyComponent={renderEmptyComponent(emptyTitle)}
      style={styles.list}
    />
  );

  const renderScene = ({ route }: { route: Route }) => {
    switch (route.key) {
      case UserTab.NEW:
        return renderUserList(newUsers, 'No people to follow');
      case UserTab.FOLLOWING:
        return renderUserList(existingUsers, 'No one followed yet');
      default:
        return null;
    }
  };

  const routes: Route[] = [
    { key: UserTab.NEW, title: 'New' },
    { key: UserTab.FOLLOWING, title: 'Following' },
  ];

  return (
    <ScreenContainer style={isOnboarding ? styles.onboardingBackground : styles.background}>
      {isOnboarding ? (
        <>
          <PrimaryHeadline style={styles.headline}>Follow Your Friends</PrimaryHeadline>
          <Divider style={styles.divider} />
        </>
      ) : null}
      {renderHeaderComponent()}
      {isOnboarding ? (
        renderUserList(newUsers, 'No people to follow')
      ) : (
        <View style={styles.tabContainer}>
          <TabView
            routes={routes}
            renderScene={renderScene}
            onIndexChange={(index) => setActiveTab(routes[index].key as UserTab)}
          />
        </View>
      )}
      {isOnboarding ? (
        <>
          <Divider style={styles.divider} />
          <View style={styles.buttonContainer}>
            <BigButton
              mode="contained"
              onPress={appNavigation.openOnboardingGroups}
              style={styles.button}
              testID="screens.FollowUsersScreen.Button.Continue">
              Continue
            </BigButton>
          </View>
        </>
      ) : null}
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  background: {
    justifyContent: 'flex-start',
  },
  button: {
    alignSelf: 'center',
  },
  buttonContainer: {
    padding: 10,
    paddingHorizontal: 20,
    width: '100%',
  },
  divider: {
    width: '100%',
  },
  headline: {
    paddingVertical: 10,
    textAlign: 'center',
  },
  inviteContactsButton: {
    justifyContent: 'center',
  },
  inviteContactsContainer: {
    justifyContent: 'center',
    paddingBottom: 5,
    paddingTop: 5,
  },
  inviteContactsText: {
    paddingLeft: 5,
  },
  list: {
    flex: 1,
    width: '100%',
  },
  onboardingBackground: {
    maxHeight: Dimensions.get('window').height,
  },
  onboardingSearchBar: {
    alignSelf: 'stretch',
    margin: 5,
    marginVertical: 10,
  },
  searchBar: {
    alignSelf: 'stretch',
    margin: 5,
    marginBottom: 5,
    marginTop: 10,
  },
  tabContainer: {
    flex: 1,
    width: '100%',
  },
});
